import { useState } from 'react';
import { useNotify } from 'react-admin';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// ─── Types ──────────────────────────────────────────────

interface UploadResult {
  movie: string;
  year: number;
  status: 'saved' | 'not_found' | 'error';
  entityId?: string;
  error?: string;
}

interface BulkResult {
  total: number;
  saved: number;
  notFound: number;
  errors: number;
  details: UploadResult[];
}

// ─── Component ──────────────────────────────────────────

export const TriviaUpload = () => {
  const notify = useNotify();

  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<BulkResult | null>(null);
  const [error, setError] = useState('');
  const [fileCount, setFileCount] = useState(0);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    setResults(null);
    setFileCount(files.length);

    try {
      // Read all JSON files
      const items: Array<{ movie: string; year: number; category: string; trivia: unknown }> = [];

      for (const file of Array.from(files)) {
        if (!file.name.endsWith('.json')) continue;

        const text = await file.text();
        let data: { movie?: string; year?: number; category?: string; trivia?: unknown };

        try {
          data = JSON.parse(text);
        } catch {
          console.warn(`Skipped invalid JSON: ${file.name}`);
          continue;
        }

        if (data.movie && data.year && data.category && data.trivia) {
          items.push({
            movie: data.movie,
            year: data.year,
            category: data.category,
            trivia: data.trivia,
          });
        }
      }

      if (items.length === 0) {
        setError('No valid trivia files found. Files must have movie, year, category, and trivia fields.');
        setUploading(false);
        return;
      }

      if (items.length > 100) {
        setError(`Too many files (${items.length}). Maximum 100 per upload. Please select fewer files.`);
        setUploading(false);
        return;
      }

      // Single file → single upload, multiple → bulk
      if (items.length === 1) {
        const item = items[0];
        const res = await fetch(`${API_BASE}/admin/api/trivia/upload`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        });

        if (!res.ok) {
          let message = 'Upload failed';
          try {
            const data = await res.json();
            message = data.message || message;
          } catch { /* non-JSON */ }
          setError(message);
        } else {
          const data = await res.json();
          setResults({
            total: 1,
            saved: 1,
            notFound: 0,
            errors: 0,
            details: [{ movie: item.movie, year: item.year, status: 'saved', entityId: data.entityId }],
          });
          notify('Trivia uploaded!', { type: 'success' });
        }
      } else {
        // Bulk upload
        const res = await fetch(`${API_BASE}/admin/api/trivia/bulk-upload`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          let message = 'Bulk upload failed';
          try {
            const data = await res.json();
            message = data.message || message;
          } catch { /* non-JSON */ }
          setError(message);
        } else {
          const data: BulkResult = await res.json();
          setResults(data);
          notify(`Uploaded: ${data.saved} saved, ${data.notFound} not found, ${data.errors} errors`, {
            type: data.errors > 0 ? 'warning' : 'success',
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.location.href = '#/game-configs')}
        >
          Back
        </Button>
        <Typography variant="h4">Upload Movie Trivia</Typography>
      </Box>

      {/* Upload Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Trivia JSON Files
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select one or more trivia JSON files generated by the LLM pipeline.
            Each file must contain: movie (title), year, category, and trivia data.
            Files are matched to entities by title + year + category.
          </Typography>

          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            disabled={uploading}
          >
            {uploading ? `Uploading ${fileCount} file(s)...` : 'Select JSON Files'}
            <input
              type="file"
              accept=".json"
              multiple
              hidden
              onChange={handleFileUpload}
            />
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Supports single or bulk upload (multiple files)
          </Typography>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {results && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upload Results
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={`Saved: ${results.saved}`} color="success" />
              {results.notFound > 0 && (
                <Chip label={`Not Found: ${results.notFound}`} color="warning" />
              )}
              {results.errors > 0 && (
                <Chip label={`Errors: ${results.errors}`} color="error" />
              )}
              <Chip label={`Total: ${results.total}`} variant="outlined" />
            </Box>

            {results.details.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Movie</TableCell>
                      <TableCell>Year</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Entity ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.details.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{d.movie}</TableCell>
                        <TableCell>{d.year}</TableCell>
                        <TableCell>
                          <Chip
                            label={d.status}
                            size="small"
                            color={
                              d.status === 'saved' ? 'success' :
                              d.status === 'not_found' ? 'warning' : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {d.entityId ? (
                            <Typography variant="caption" fontFamily="monospace">
                              {d.entityId.substring(0, 8)}...
                            </Typography>
                          ) : (
                            d.error || '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
